import { useEffect, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '../components/AuthProvider'
import { Icon } from '../components/Icon'
import { cognitoConfigured, environment } from '../lib/env'
import type { Role } from '../types'

const capabilities = [
  ['clock', 'Weekly time', 'Fast, structured reporting for every active engagement.'],
  ['receipt', 'Receipts with context', 'Secure uploads and explainable review signals.'],
  ['team', 'Human approvals', 'Clear queues, decisions, and an immutable audit trail.']
] as const

export const LandingPage = () => {
  const { demoSignIn, session, signIn } = useAuth()
  const navigate = useNavigate()
  const [error, setError] = useState<string | null>(null)
  const [isSigningIn, setSigningIn] = useState(false)

  if (session) return <Navigate to="/dashboard" replace />

  const startSignIn = async () => {
    setError(null)
    setSigningIn(true)
    try {
      await signIn()
    } catch (issue) {
      setError(issue instanceof Error ? issue.message : 'We could not start sign-in.')
      setSigningIn(false)
    }
  }

  const openDemo = (role: Role) => {
    demoSignIn(role)
    navigate('/dashboard')
  }

  return (
    <main className="landing-page">
      <div className="landing-ambient landing-ambient-one" />
      <div className="landing-ambient landing-ambient-two" />
      <header className="landing-header">
        <div className="brand-lockup landing-brand">
          <div className="brand-mark" aria-hidden="true"><span>Q</span></div>
          <div><strong>Quantum</strong><small>TIME &amp; EXPENSE</small></div>
        </div>
        <div className="landing-header-note"><Icon name="shield" size={16} /> Protected workspace</div>
      </header>

      <section className="landing-grid">
        <div className="landing-copy">
          <p className="eyebrow">TIME THAT ADDS UP</p>
          <h1>Work, spend, and approvals—<em>in one clear rhythm.</em></h1>
          <p className="landing-lede">
            Quantum Time &amp; Expense gives teams a calm, accountable way to log work, submit receipts, and keep decisions moving.
          </p>
          <div className="landing-actions">
            {cognitoConfigured ? (
              <button className="button button-primary button-large" onClick={startSignIn} disabled={isSigningIn}>
                {isSigningIn ? 'Taking you to sign in…' : 'Sign in securely'} <Icon name="arrow-right" size={18} />
              </button>
            ) : (
              <div className="configuration-note">
                <Icon name="settings" size={18} />
                <span>Cognito will be available after the first environment deployment.</span>
              </div>
            )}
            {error && <p className="form-error" role="alert">{error}</p>}
          </div>
          <div className="trust-row">
            <span><Icon name="check" size={15} /> MFA protected</span>
            <span><Icon name="check" size={15} /> Role-aware</span>
            <span><Icon name="check" size={15} /> Audit-ready</span>
          </div>
        </div>

        <div className="landing-preview" aria-label="Product preview">
          <div className="preview-glow" />
          <div className="preview-browser">
            <div className="preview-browser-bar"><span /><span /><span /><div>qte.quantuminsights.us</div></div>
            <div className="preview-interface">
              <div className="preview-rail"><div className="preview-q">Q</div><i /><i /><i /><i /></div>
              <div className="preview-content">
                <div className="preview-welcome"><div><small>MONDAY, SEPTEMBER 9</small><strong>Good morning, Jordan</strong></div><span className="preview-avatar">JL</span></div>
                <div className="preview-metrics"><div><small>THIS WEEK</small><strong>34.5 <em>h</em></strong><span>5.5 h remaining</span></div><div><small>EXPENSES</small><strong>$481<em>.35</em></strong><span>1 needs attention</span></div></div>
                <div className="preview-line-chart"><div className="preview-chart-title"><span>Time this week</span><b>75%</b></div><svg viewBox="0 0 300 105" preserveAspectRatio="none"><defs><linearGradient id="chartFill" x1="0" x2="0" y1="0" y2="1"><stop stopColor="#70d6be" stopOpacity=".28"/><stop offset="1" stopColor="#70d6be" stopOpacity="0"/></linearGradient></defs><path d="M0 82 C28 76, 41 79, 58 55 S98 78, 121 51 S156 62, 179 43 S216 67, 245 27 S280 36, 300 11 L300 105 L0 105Z" fill="url(#chartFill)"/><path d="M0 82 C28 76, 41 79, 58 55 S98 78, 121 51 S156 62, 179 43 S216 67, 245 27 S280 36, 300 11" fill="none" stroke="#70d6be" strokeWidth="3"/></svg></div>
                <div className="preview-task"><span className="preview-task-dot" /><div><strong>Review Sep 7 – Sep 13</strong><small>Your timesheet is ready for attestation</small></div><button>Review</button></div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-capabilities">
        {capabilities.map(([icon, title, description]) => (
          <article key={title}>
            <span className="capability-icon"><Icon name={icon} size={20} /></span>
            <div><strong>{title}</strong><p>{description}</p></div>
          </article>
        ))}
      </section>

      {environment.demoMode && (
        <section className="demo-strip" aria-label="Visual foundation demonstrations">
          <div><span className="demo-label"><Icon name="sparkle" size={15} /> FOUNDATION PREVIEW</span><p>Explore the shell while AWS services are being connected.</p></div>
          <div className="demo-actions">
            <button onClick={() => openDemo('Employee')}>Employee view</button>
            <button onClick={() => openDemo('Manager')}>Manager view</button>
            <button onClick={() => openDemo('Admin')}>Admin view</button>
          </div>
        </section>
      )}

      <footer className="landing-footer"><span>© {new Date().getFullYear()} Quantum Insights</span><span>Confidential internal application</span></footer>
    </main>
  )
}

export const AuthCallbackPage = () => {
  const { completeSignIn } = useAuth()
  const location = useLocation()
  const navigate = useNavigate()
  const [message, setMessage] = useState('Verifying your secure sign-in…')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let active = true
    void completeSignIn(location.search)
      .then((session) => {
        if (!active) return
        if (session.user.roles.length === 0) {
          navigate('/access-denied', { replace: true })
          return
        }
        setMessage('Welcome back. Opening your workspace…')
        window.setTimeout(() => navigate('/dashboard', { replace: true }), 350)
      })
      .catch((issue) => active && setError(issue instanceof Error ? issue.message : 'We could not complete sign-in.'))
    return () => {
      active = false
    }
  }, [completeSignIn, location.search, navigate])

  return (
    <main className="auth-state-page">
      <div className="auth-state-card">
        <div className={`auth-orbit ${error ? 'has-error' : ''}`}><span className="brand-mark"><span>Q</span></span></div>
        <p className="eyebrow">SECURE SIGN-IN</p>
        <h1>{error ? 'Sign-in needs another try' : message}</h1>
        <p>{error || 'Your organization and permissions are being checked.'}</p>
        {error && <button className="button button-primary" onClick={() => navigate('/', { replace: true })}>Return to sign in</button>}
      </div>
    </main>
  )
}

export const AccessDeniedPage = () => {
  const { session, signOut } = useAuth()
  const navigate = useNavigate()
  return (
    <main className="auth-state-page">
      <div className="auth-state-card">
        <div className="denied-icon"><Icon name="shield" size={30} /></div>
        <p className="eyebrow">ACCESS NOT CONFIGURED</p>
        <h1>Your account needs a QTE role.</h1>
        <p>{session?.user.email || 'This account'} signed in successfully, but it is not in an Employee, Manager, or Admin group.</p>
        <div className="button-row">
          <button className="button button-primary" onClick={() => signOut({ returnToIdentityProvider: !session?.isDemo })}>Sign out</button>
          <button className="button button-secondary" onClick={() => navigate('/')}>Back</button>
        </div>
      </div>
    </main>
  )
}
